import { createFileRoute } from '@tanstack/react-router';
import { BlogPost } from '@/components/astro/blog/BlogPost';
import { BlogContent } from '@/components/astro/blog/blog-content';
import { normalizeCanonicalUrl } from '@/seo/canonical';
import { seo } from '@/seo/seo';
import { callApiPromise } from '@/runtimes/get-runtime';
import { cacheHeadersFor, experimentalRoutes } from '@/analytics';

// stale-if-error keeps a working page in front of users when WordPress upstream
// is timing out, so a WP Engine origin blip doesn't surface as a 500.
const BLOG_POST_CACHE_CONTROL =
  'public, max-age=0, s-maxage=3600, stale-while-revalidate=86400, stale-if-error=86400';

const blogPostRouteAnalytics = experimentalRoutes['/_marketing/blog/$slug'];

export const Route = createFileRoute('/_marketing/blog/$slug')({
  staticData: { analytics: blogPostRouteAnalytics },
  headers: cacheHeadersFor(blogPostRouteAnalytics, BLOG_POST_CACHE_CONTROL),
  loader: ({ params, abortController }) =>
    callApiPromise(
      (api) => api.Blog.GetPost({ params: { slug: params.slug } }),
      {
        signal: abortController.signal,
      },
    ),
  head: ({ loaderData, params }) => {
    const post = loaderData?.detailPage.oldPostFormat;
    const title = post?.title ?? 'Blog';
    const seoDesc = post?.seoDescription;
    const rawExcerpt = post?.excerpt ?? '';
    const excerptText = rawExcerpt
      ? rawExcerpt
          .replaceAll(/<[^>]+>/g, ' ')
          .replaceAll(/\s+/g, ' ')
          .trim()
      : '';
    const limitedExcerpt = excerptText ? excerptText.slice(0, 155) : '';
    const description = seoDesc ?? limitedExcerpt ?? 'Listening Blog Article';

    const heroImage = post?.heroImage;

    return {
      meta: [
        ...seo({
          title,
          description,
          image: heroImage ?? undefined,
          ogType: 'article',
        }),
      ],
      links: [
        {
          rel: 'canonical',
          href: normalizeCanonicalUrl(`/blog/${params.slug}`),
        },
      ],
    };
  },
  component: Page,
});

function Page() {
  const { detailPage, randomPosts, randomDocs } = Route.useLoaderData();
  return (
    <BlogPost
      post={detailPage.oldPostFormat}
      randomDocs={randomDocs}
      randomPosts={randomPosts}
    >
      <BlogContent detailPage={detailPage} />
    </BlogPost>
  );
}
